import { Box, Typography } from "@mui/material";
import useColors from "../../../../../api/hooks/useColors";
import AssigneeLabel from "../../../../../components/assignee/AssigneeLabel";
import CustomToolTip from "../../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { RemediationTypeChip } from "../../../../../utils/chipsData";
import { ControlStatus } from "../../../../../utils/constants";
import { getPrettyDate } from "../../../../../utils/timestamp";
import ControlStatusIndicator from "../../../../posture/controls/components/ControlStatusIndicator";
import ChipColor from "../../../../remediation/plan/components/ChipColor";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";

const RemediationPerformanceTable = ({ activityLog, isLoading, refToPass }) => {
  const colors = useColors();

  const columns = [
    {
      field: "control_id",
      headerName: "ID",
      width: 70,
      renderCell: (params) => (
        <Typography variant="body2">#{params.value}</Typography>
      ),
    },
    {
      field: "name_for_gap_state",
      headerName: "Description",
      flex: 0.4,
      minWidth: 250,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value || "N/A"}
        </Typography>
      ),
    },
    {
      field: "tool",
      headerName: "Tool",
      width: 80,
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2">-</Typography>;

        const toolKey = params.value.toLowerCase();
        const integration = IntegrationsMapping[toolKey];

        if (!integration)
          return <Typography variant="body2">{params.value}</Typography>;

        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            <CustomToolTip title={integration.displayName}>
              <Box>
                <SvgToIcon IconLogo={integration.logo} width={40} />
              </Box>
            </CustomToolTip>
          </Box>
        );
      },
    },
    {
      field: "control_severity",
      headerName: "Severity",
      width: 120,
      valueFormatter: (params) => {
        return RemediationTypeChip["controlPriority"][params.value]?.label;
      },
      renderCell: (params) => (
        <ChipColor
          value={params.value}
          type="controlPriority"
          disabled={false}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      sortComparator: (v1, v2) => {
        const statusOrder = {
          [ControlStatus.GAP]: 4,
          [ControlStatus.COMPLIANT]: 3,
          [ControlStatus.SNOOZED]: 2,
          [ControlStatus.EXCLUDED]: 1,
        };

        const value1 = statusOrder[v1] || 0;
        const value2 = statusOrder[v2] || 0;

        return value1 - value2;
      },
      renderCell: (params) => {
        return <ControlStatusIndicator status={params.row?.status} />;
      },
    },
    {
      headerName: "Improvement",
      width: 170,
      valueGetter: (params) => {
        return params.row;
      },
      valueFormatter: (params) => {
        return `${params.value?.entities_before_count} Entities → ${params.value?.entities_after_count} Entities`;
      },
      renderCell: (params) => {
        const beforeCount = params.row.entities_before_count;
        const afterCount = params.row.entities_after_count;

        if (beforeCount == null || afterCount == null) {
          return <Typography variant="body2">-</Typography>;
        }

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color={colors.text_secondary}>
              {beforeCount} Entities
            </Typography>
            <Typography variant="caption">→</Typography>
            <Typography variant="caption" color={colors.primary_600}>
              {afterCount} Entities
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "assignee",
      headerName: "Assignee",
      flex: 0.13,
      valueGetter: (params) => {
        return (
          params.row.assignee?.display_name ||
          params.row.assignee?.email ||
          params.row.assignee_email
        );
      },
      renderCell: (params) => {
        const baseStyles = {
          textTransform: "none",
          paddingLeft: 1,
          paddingRight: 1,
          width: "auto",
          border: `1px solid ${colors.grey_300}`,
          height: "24px",
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          gap: 1,
          backgroundColor: colors.grey_50,
          color: colors.text_primary,
        };

        const selectedAssignee = params.row.assignee;
        return (
          <Box sx={baseStyles}>
            <AssigneeLabel assignee={selectedAssignee} />
          </Box>
        );
      },
    },
    {
      field: "log_timestamp",
      headerName: "Date Resolved",
      width: 130,

      valueFormatter: (params) => {
        return getPrettyDate(params.value.timestamp_utc, true);
      },
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2">-</Typography>;

        return (
          <Typography variant="caption" color={colors.grey_700}>
            {params.value
              ? getPrettyDate(params.value.timestamp_utc, true)
              : "-"}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box>
      <StyledDataGrid
        rows={activityLog || []}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.log_id || Math.random()}
        pageSizeOptionsOverride={[8, 20, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 8 },
          },
        }}
        sx={{
          "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
            {
              paddingLeft: "16px",
            },
          "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
            {
              paddingRight: "16px",
            },
        }}
        apiRef={refToPass}
      />
    </Box>
  );
};

export default RemediationPerformanceTable;
